import React, { useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import classnames from 'classnames'
import type { QuoteFile } from '../../types'
import type { StyleClasses } from '../../styles'
import { noDragStyles } from '../../styles'
import { generateSrcSet, imageCrossOrigin, isSvgFile, retryCorsImageOnError, usePrintSizedSrc } from '../../util'

const useStyles = makeStyles<Theme, ResponsiveImageProps>(
  (_theme) => ({
    root: {
      color: 'blue',
      objectFit: 'contain'
    },
    image: {
      maxWidth: '100%',
      height: 'auto',
      maxHeight: '100%',
      objectFit: 'contain',
      // See noDragStyles (#23509) — draggable={false} on each <img> below is
      // still required for Firefox, which ignores WebkitUserDrag.
      ...noDragStyles
    }
  }),
  { name: 'ResponsiveImage' }
)

export interface ResponsiveImageProps {
  classes?: StyleClasses<typeof useStyles>
  file: QuoteFile
  size?: number
  page?: number
  onClick: (ev: any) => any
  omitSizes?: string[]
}

function ResponsiveImage(props: ResponsiveImageProps) {
  const classes = useStyles(props)
  const { file, onClick, size, omitSizes } = props
  const imageRef = useRef<HTMLImageElement>(null)
  const printSized = usePrintSizedSrc(file, imageRef)

  // Need to organize or src url here for image specific sizing and type demands.
  // `file.type` is optional and often absent on migrated records — isSvgFile falls back to
  // `format` and the src extension rather than throwing (#23950).
  if (isSvgFile(file)) {
    const generatedSrc = generateSrcSet({ file, svg: true })
    return (
      <>
        <img
          ref={imageRef}
          draggable={false}
          crossOrigin={imageCrossOrigin(file)}
          className={classnames(classes.fileRoot, classes.image, classes.root)}
          src={generatedSrc}
          onClick={onClick}
          onError={retryCorsImageOnError}
          width={'auto'}
        />
      </>
    )
  }

  if (size) {
    // Maybe not?
    const generatedSrc = generateSrcSet({ file, size })
    return (
      <>
        <img
          ref={imageRef}
          draggable={false}
          crossOrigin={imageCrossOrigin(file)}
          className={classnames(classes.fileRoot, classes.image)}
          src={generatedSrc}
          onClick={onClick}
          onError={retryCorsImageOnError}
        />
      </>
    )
  }

  // A PDF render loads the variant that fits the slot, not the viewport-sized one below: the
  // viewport breakpoints picked 900px for ~154px thumbnails and made 71 MB PDFs (#25493).
  if (printSized.pending || printSized.src) {
    return (
      <img
        ref={imageRef}
        draggable={false}
        crossOrigin={imageCrossOrigin(file)}
        src={printSized.src ?? undefined}
        // Falls back to the original if the variant is missing, then to the CORS retry
        onError={printSized.onError}
        className={classnames(classes.fileRoot, classes.image)}
        onClick={onClick}
      />
    )
  }

  const generatedSrc = generateSrcSet({ file })
  const allSizes = [
    { media: '(max-width: 400px)', size: '300px' },
    { media: '(max-width: 600px)', size: '500px' },
    { media: '(max-width: 800px)', size: '700px' },
    { media: '(max-width: 1000px)', size: '900px' },
    { media: '', size: '1200px' }
  ]
  const filteredSizes = allSizes
    .filter(({ size }) => !omitSizes?.includes(size))
    .map(({ media, size }) => (media ? `${media} ${size}` : size))
    .join(',\n')

  return (
    <>
      <img
        ref={imageRef}
        draggable={false}
        crossOrigin={imageCrossOrigin(file)}
        srcSet={generatedSrc}
        sizes={filteredSizes}
        src={file.src}
        onError={(e) => {
          // If the Lambda-resized size isn't ready yet, fall back to the original upload
          if (e.currentTarget.srcset) {
            e.currentTarget.srcset = ''
            return
          }
          // Original also failed — could be a Safari CORS-poisoned cache entry.
          retryCorsImageOnError(e)
        }}
        className={classnames(classes.fileRoot, classes.image)}
        onClick={onClick}
      />
    </>
  )
}

export default ResponsiveImage
